import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/cards/card/card";
import { ContentCard, Blocks } from "@/types/cms";
import { renderRichTextWithMutedStrikethrough, getFirstHeadingFromBlocks } from "@/lib/richText";
import * as Icons from "lucide-react";
import ResponsiveImage, { type ImageSource } from "@/components/utilities/ResponsiveImage";
import { optimizeVideoUrlAutoGood, getCloudinaryVideoPosterUrl } from "@/lib/imageOptimization";
import { optimizeCloudinaryUrl } from "@/lib/videoConfig";
import VideoPlayer from "@/components/video/VideoPlayer";
import CardPrice from "@/components/cards/cardPrice/cardPrice";
import TestimonialCard from "@/components/cards/testimonialCard/TestimonialCard";
import { cn } from "@/lib/utils";
import { isDialogCard } from "@/lib/cardCustomId";
import { CARD_SIZES_3UP, DIALOG_IMAGE_SIZES } from "@/lib/imageSizes";
import MinimalDialog, {
  MinimalDialogBody,
  MinimalDialogHiddenTitle,
  MinimalDialogTitle,
} from "@/components/dialog/minimalDialog/MinimalDialog";
import styles from "./cardContent.module.css";

interface CardContentItemProps {
  card: ContentCard;
  icon?: string;
  title?: string;
  subtitle?: string;
  body?: Blocks | string;
  shadow?: boolean;
  variant?: "default" | "testimonial" | "price";
  /**
   * `sizes` for the card image. Defaults to the LEAN 3-up case on purpose:
   * a call site that forgets to pass a larger value renders soft rather than
   * silently downloading a 2560px file for a small slot. Large contexts
   * (hero, 1-up, 2-up) pass their own explicitly.
   */
  imageSizes?: string;
  /** Raise only for large renders (hero, dialog). Allowlisted in next.config. */
  quality?: number;
  /** Set on the LCP image (the hero) so it isn't lazy-loaded. */
  priority?: boolean;
}

const CardContentItem = ({ card, icon, title, subtitle, body, shadow = false, variant = "default", imageSizes = CARD_SIZES_3UP, quality, priority }: CardContentItemProps) => {
  const blocksFromCard = Array.isArray(card.content)
    ? card.content
    : Array.isArray(card.body)
      ? card.body
      : undefined;
  const overrideBlocks = Array.isArray(body) ? (body as Blocks) : undefined;
  const blocks = overrideBlocks || blocksFromCard;

  const headerFromBlocks = blocks ? getFirstHeadingFromBlocks(blocks) : undefined;
  const headerText = title ?? headerFromBlocks ?? card.header ?? "";
  const headingBlock = blocks?.find((b: any) => b.type === 'heading');
  const headingLevel = (headingBlock as any)?.level ?? 2;
  
  // Check if heading contains any italic text
  const hasItalicText = headingBlock?.children?.some((child: any) => child.italic) ?? false;
  
  const headingClass =
    headingLevel === 1
      ? "title-2xl"
      : headingLevel === 2
      ? "title-xl"
      : headingLevel === 3
      ? "title-lg"
      : headingLevel === 4
      ? "title-md"
      : headingLevel === 5
      ? "title-sm"
      : "title-xs";

  const accentClassForHeading =
    headingLevel === 1
      ? "accent-2xl"
      : headingLevel === 2
      ? "accent-xl"
      : headingLevel === 3
      ? "accent-lg"
      : headingLevel === 4
      ? "accent-md"
      : headingLevel === 5
      ? "accent-sm"
      : "accent-xs";
  
  // If heading has italic text, use accent class for line-height
  const finalHeadingClass = hasItalicText ? accentClassForHeading : headingClass;

  const headingInline = headingBlock ? (
    <span>
      {headingBlock.children?.map((child: any, idx: number) => {
        let content: React.ReactNode = child.text;
        if (child.bold) content = <strong>{content}</strong>;
        if (child.italic) {
          content = <span className={accentClassForHeading}>{content}</span>;
        }
        if (child.underline) content = <u>{content}</u>;
        if (child.type === 'link' && child.url) {
          const linkText = (Array.isArray(child.children) ? child.children.map((c: any) => c.text).join('') : child.text) || '';
          content = (
            <a href={child.url} target="_blank" rel="noopener noreferrer" style={{ whiteSpace: 'pre-wrap' }}>
              {linkText}
            </a>
          );
        }
        const spanClass = child.strikethrough ? 'muted' : '';
        return (
          <span key={idx} className={spanClass} style={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </span>
        );
      })}
    </span>
  ) : null;

  // Remove the first heading block from body rendering
  const bodyBlocks = blocks
    ? blocks.filter((block, index) => block.type !== 'heading' || index !== blocks.findIndex(b => b.type === 'heading'))
    : undefined;

  const toPascalCase = (name: string) =>
    name
      .replace(/[-_ ]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');

  const resolveIcon = (rawName?: string) => {
    if (!rawName) return undefined;
    const aliases: Record<string, string[]> = {
      'circle-question-mark': ['CircleHelp', 'HelpCircle'],
      'question-circle': ['CircleHelp', 'HelpCircle'],
      'question': ['CircleHelp', 'HelpCircle'],
      'help': ['CircleHelp', 'HelpCircle'],
    };
    const normalized = rawName.trim().toLowerCase();
    const candidates: string[] = [];
    if (aliases[normalized]) candidates.push(...aliases[normalized]);
    const pascal = toPascalCase(rawName);
    candidates.push(pascal);
    // Try common suffix/prefix variations
    if (!/Circle$/i.test(pascal)) candidates.push(`${pascal}Circle`);
    if (/Circle$/i.test(pascal)) candidates.push(pascal.replace(/Circle$/i, ''));

    const dict = Icons as unknown as Record<string, React.ComponentType<any>>;
    for (const key of candidates) {
      if (dict[key]) return dict[key];
    }
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[cardContent] Unknown icon: "${rawName}". Tried: ${candidates.join(', ')}`);
    }
    return undefined;
  };

  // Price variant: use dedicated CardPrice component.
  // If the card's custom ID contains both "price" and "free", render the
  // free-price layout (first body paragraph moves above the CTA).
  if (variant === "price") {
    const ccid = String((card as any)?.contentCard_customID ?? "")
      .toLowerCase()
      .trim();
    const freeVariant = ccid.includes("price") && ccid.includes("free");
    return <CardPrice card={card} freeVariant={freeVariant} />;
  }

  // Testimonial variant: delegate to dedicated TestimonialCard component
  if (variant === "testimonial") {
    return <TestimonialCard card={card} body={body} />;
  }

  // Default variant rendering
  const IconComponent = resolveIcon(icon ?? card.icon ?? undefined);

  // Per-card text justification (falls back to left)
  // Dialog cards are interactive, so the title takes a link affordance on
  // card hover. Computed here so both the root and the title can use it.
  const isDialog = isDialogCard(card);

  const cardTextJustify =
    ((card as any)?.textJustify as "left" | "center" | "right" | undefined) ||
    "left";

  const alignmentClass =
    cardTextJustify === "center"
      ? styles.alignCenter
      : cardTextJustify === "right"
        ? styles.alignRight
        : styles.alignLeft;

  return (
    <Card className={cn(styles.root, isDialog && styles.dialogCard)}>
      <CardHeader className={cn(styles.header, alignmentClass)}>
        {/* Icon support - render icon above header if provided */}
        {IconComponent && (
          <div className={styles.iconWrapper}>
            <IconComponent className={styles.icon} strokeWidth={1.5} />
          </div>
        )}
        
        {/* Video support - check for video field first */}
        {(() => {
          const videoField = (card as any).video;
          const videoData = videoField?.data?.attributes || videoField;
          const videoUrl = videoData?.url;

          if (videoUrl) {
            const optimizedVideoUrl = optimizeVideoUrlAutoGood(videoUrl) ?? videoUrl;
            const posterUrl = getCloudinaryVideoPosterUrl(videoUrl);
            
            return (
              <VideoPlayer
                src={optimizedVideoUrl}
                poster={posterUrl}
                videoClassName={cn(styles.image, shadow && "shadow-card")}
              />
            );
          }

          // Fall back to image if no video
          const baseDesktop = (card as any).image;
          const baseMobile = (card as any).imageMobile;

          const desktop =
            ((card as any).imageDesktop as any)?.data?.attributes ||
            ((card as any).imageDesktop as any) ||
            (baseDesktop as any)?.data?.attributes ||
            (baseDesktop as any);

          const tablet =
            ((card as any).imageTablet as any)?.data?.attributes ||
            ((card as any).imageTablet as any);

          const mobile =
            ((card as any).imageMobile as any)?.data?.attributes ||
            ((card as any).imageMobile as any) ||
            (baseMobile as any)?.data?.attributes ||
            (baseMobile as any);

          const desktopUrl = desktop?.url as string | undefined;
          const tabletUrl = tablet?.url as string | undefined;
          const mobileUrl = mobile?.url as string | undefined;
          const anyUrl = desktopUrl || tabletUrl || mobileUrl || "";
          if (!anyUrl) return null;

          // Build ImageSource objects so <Image> gets the *real* Cloudinary
          // dimensions — otherwise CSS `height: auto` derives height from a
          // fallback ratio and the image gets squashed into 3:2.
          const toImageSource = (
            node: any,
            url: string | undefined
          ): ImageSource | null => {
            if (!url) return null;
            const width = Number(node?.width) || 0;
            const height = Number(node?.height) || 0;
            const src = optimizeCloudinaryUrl(url, false);
            if (!width || !height) {
              // Payload almost always ships width/height on media uploads.
              // If it's missing for some reason, 1200×800 is the historical
              // default — better than crashing <Image>.
              return { src, width: 1200, height: 800 };
            }
            return { src, width, height };
          };

          const desktopSource = toImageSource(desktop, desktopUrl);
          const tabletSource = toImageSource(tablet, tabletUrl);
          const mobileSource = toImageSource(mobile, mobileUrl);

          // If the media URL is actually a video (e.g. Cloudinary video attached to image field),
          // render it with the VideoPlayer instead of as an image.
          const isVideoFromImageField = /\.(mp4|mov|webm|avi|m4v)$/i.test(anyUrl);
          if (isVideoFromImageField) {
            const optimizedVideoUrl = optimizeVideoUrlAutoGood(anyUrl) ?? anyUrl;
            const posterUrl = getCloudinaryVideoPosterUrl(anyUrl);

            return (
              <VideoPlayer
                src={optimizedVideoUrl}
                poster={posterUrl}
                videoClassName={cn(styles.image, shadow && "shadow-card")}
              />
            );
          }

          const imageNode = (
            <ResponsiveImage
              desktop={desktopSource}
              tablet={tabletSource}
              mobile={mobileSource}
              alt={headerText || "Card image"}
              className={cn(styles.image, shadow && "shadow-card")}
              sizes={imageSizes}
              quality={quality}
              priority={priority}
            />
          );

          if (!isDialog) return imageNode;

          // Dialog cards: the image becomes the trigger. The SAME node is
          // reused inside the dialog, so src/srcset/sizes are identical and
          // the browser serves the already-decoded bitmap rather than
          // refetching at a different candidate size.
          // The dialog renders at up to 720 CSS px (1440 at 2x), the grid slot
          // at a fraction of that. One shared node would force a single
          // `sizes` to serve both, so the dialog gets its own, at a higher
          // quality. (The shared-node trick existed for the morph animation,
          // which has since been removed.)
          const dialogImageNode = (
            <ResponsiveImage
              desktop={desktopSource}
              tablet={tabletSource}
              mobile={mobileSource}
              alt={headerText || "Card image"}
              className={styles.image}
              sizes={DIALOG_IMAGE_SIZES}
              quality={90}
            />
          );

          return (
            <MinimalDialog
              media={imageNode}
              dialogMedia={dialogImageNode}
              label={`Open ${headerText || "details"}`}
            >
              {headerText ? (
                <MinimalDialogTitle className={finalHeadingClass}>
                  {headingInline ?? headerText}
                </MinimalDialogTitle>
              ) : (
                // Radix warns (and screen readers suffer) without a Title.
                <MinimalDialogHiddenTitle>
                  {card.title || "Details"}
                </MinimalDialogHiddenTitle>
              )}
              {bodyBlocks ? (
                <MinimalDialogBody className="body-lg">
                  {renderRichTextWithMutedStrikethrough(bodyBlocks, {
                    defaultAlign: cardTextJustify,
                  })}
                </MinimalDialogBody>
              ) : null}
            </MinimalDialog>
          );
        })()}

        <CardTitle className={cn(finalHeadingClass, isDialog && styles.dialogTitle)}>
          {headingInline ?? headerText}
        </CardTitle>
        {(subtitle ?? card.subheader) && (
          <CardDescription className="title-sm">
            {subtitle ?? card.subheader}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className={alignmentClass}>
        <CardDescription className={cn("body-lg", styles.body, alignmentClass)}>
          {bodyBlocks
            ? renderRichTextWithMutedStrikethrough(bodyBlocks, {
                defaultAlign: cardTextJustify,
              })
            : typeof body === 'string'
              ? body
              : typeof card.body === 'string'
                ? card.body
                : null}
        </CardDescription>
      </CardContent>
    </Card>
  );
};

export default CardContentItem;
